import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useRef, useState } from "react";
import { logger } from "../../observability/LoggerFactory";
import { HiddenFileInput } from "../../ui/HiddenFileInput";
import { appendCsvContent } from "./AppendCsvContent";
import type { FormFields } from "./useSiteImport";

interface SiteImportFormProps {
  onSubmit: (form: FormFields) => void;
}

const PLACEHOLDER = [
  "CSV формат (порядок колонок произвольный):",
  "Обязательные: URL, Login, Password",
  "Опциональные: BlogId, DR, OrganicKeywords, Age",
  "",
  "URL,Login,Password,DR,OrganicKeywords,Age",
  "https://site.com,login,password,25,500,3",
  "https://site2.com,login2,pass2,,,",
].join("\n");

export function SiteImportForm(props: SiteImportFormProps) {
  const [rawData, setRawData] = useState("");
  const [seller, setSeller] = useState("");
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canSubmit = rawData.trim().length > 0 && seller.trim().length > 0;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    props.onSubmit({ rawData, seller });
  };

  const loadFile = async (file: File) => {
    setFileError(null);
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setFileError("Можно загружать только CSV-файлы.");
      return;
    }
    try {
      const text = await file.text();
      setRawData((previous) => appendCsvContent(previous, text));
    } catch (error) {
      logger.error({ component: "SiteImportForm", err: error }, "Failed to read CSV file");
      setFileError("Не удалось прочитать файл. Попробуйте ещё раз.");
    }
  };

  const openFileDialog = () => {
    const input = fileInputRef.current;
    if (input) {
      input.click();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      void loadFile(files[0]);
    }
    event.target.value = "";
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      void loadFile(files[0]);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Импорт сайтов
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          label="Список доступов"
          value={rawData}
          onChange={(event) => setRawData(event.target.value)}
          multiline
          minRows={8}
          maxRows={20}
          fullWidth
          size="small"
          placeholder={PLACEHOLDER}
          sx={{ mb: 2 }}
        />

        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{
            mb: 2,
            p: 2,
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 1,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" gutterBottom>
            Перетащите CSV-файл сюда или
          </Typography>
          <Button variant="outlined" size="small" onClick={openFileDialog}>
            Загрузить CSV
          </Button>
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mt: 1 }}
          >
            Формат: URL,Login,Password
          </Typography>
          {fileError ? (
            <Typography
              variant="caption"
              display="block"
              color="error"
              sx={{ mt: 1 }}
            >
              {fileError}
            </Typography>
          ) : null}
          <HiddenFileInput
            inputRef={fileInputRef}
            accept=".csv,text/csv"
            onFileChosen={handleInputChange}
          />
        </Box>

        <TextField
          label="Селлер"
          value={seller}
          onChange={(event) => setSeller(event.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        />

        <Box display="flex" gap={1}>
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            Парсить
          </Button>
        </Box>
      </form>
    </Box>
  );
}
